import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import Config from '../../lib/config';
import { parseMarkdown } from '../lib/markdown';
import UnauthedHeader from '../components/Headers/UnauthedHeader';
import PageContainer from '../components/Containers/PageContainer';
import Button from '../components/Button';
import IllustrationCheckmark from '../svgs/IllustrationCheckMark';
import IllustrationWarning from '../svgs/IllustrationWarning';
import Spinner from '../svgs/Spinner';
import ToastAlert from '../components/ToastAlert';
import InputGroup from '../components/InputGroup';
import CompletedSignature from '../components/CompletedSignature';
import SignatureFlagName from '../svgs/SignatureFlagName';
import SignatureFlagRole from '../svgs/SignatureFlagRole';
import { getTimeStamp } from '../lib/time';

const API_BASE = Config.REACT_APP_HOMECOMING_API_BASE_URI;
const FORM_SUBMISSION_URL = `${API_BASE}/api/v2/form-submission`;

type ProviderBranding = {
  primary_color: string;
  secondary_color: string;
  wordmark_logo_url: string;
  square_logo_url: string;
};

type FormPayload = {
  program_assessment_id: string;
  form_name: string;
  instructional_text?: string | null;
  content: {
    questions?: Array<{
      question: string;
      questionType?: string;
      isRequired?: boolean;
      allowedExtensions?: string[];
    }>;
  };
  schema_type: string;
  patient_first_name?: string | null;
  patient_full_name?: string | null;
  provider_name?: string | null;
  recipient_name?: string | null;
  recipient_email?: string | null;
  branding?: ProviderBranding | null;
};

type AnswerInput = {
  question: string;
  questionType: string;
  answer?: string;
  answers?: string[];
  mediaId?: string;
  fileName?: string;
};

const FormSubmission: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [payload, setPayload] = useState<FormPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [fileAnswers, setFileAnswers] = useState<
    Record<number, { mediaId: string; fileName: string }>
  >({});
  const [uploadingFileIdx, setUploadingFileIdx] = useState<number | null>(null);
  const [completerName, setCompleterName] = useState('');
  const [completerEmail, setCompleterEmail] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signatureRole, setSignatureRole] = useState('');

  const questions = payload?.content?.questions ?? [];
  const hasProviderSignatureQuestion = questions.some(
    (q) => q.questionType === 'ProviderSignature',
  );
  const signatureMatchesRecipient = useMemo(
    () =>
      completerName.trim().toLowerCase() === signatureName.trim().toLowerCase(),
    [completerName, signatureName],
  );
  const allRequiredAnswered = useMemo(() => {
    const questionsSatisfied = questions.every((q, idx) => {
      if (
        q.isRequired === false ||
        q.questionType === 'Statement' ||
        q.questionType === 'ProviderSignature'
      )
        return true;
      if (q.questionType === 'FileUpload') return !!fileAnswers[idx];
      return !!String(answers[idx] ?? '').trim();
    });
    const signatureSatisfied =
      !hasProviderSignatureQuestion ||
      (!!signatureName.trim() &&
        !!signatureRole.trim() &&
        signatureMatchesRecipient);
    return questionsSatisfied && signatureSatisfied;
  }, [
    questions,
    answers,
    fileAnswers,
    hasProviderSignatureQuestion,
    signatureName,
    signatureRole,
    signatureMatchesRecipient,
  ]);

  const handleFileUpload = async (questionIdx: number, file: File) => {
    if (!token) return;
    setUploadingFileIdx(questionIdx);
    const formData = new FormData();
    formData.append('token', token);
    formData.append('file', file);
    try {
      const res = await fetch(`${FORM_SUBMISSION_URL}/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        let message = 'Upload failed';
        try {
          const errorData = await res.json();
          message = errorData?.message ?? message;
        } catch {
          const text = await res.text();
          if (text) message = text;
        }
        throw new Error(message);
      }
      const data = await res.json();
      setFileAnswers((prev) => ({
        ...prev,
        [questionIdx]: {
          mediaId: data.media_id,
          fileName: data.file_name ?? file.name,
        },
      }));
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={
            err instanceof Error
              ? err.message
              : 'File upload failed. Please try again.'
          }
          level="error"
        />
      ));
    } finally {
      setUploadingFileIdx(null);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Missing link');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${FORM_SUBMISSION_URL}?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          let message = 'Invalid or expired link';
          try {
            const d = await res.json();
            message = d?.message ?? message;
          } catch {
            const text = await res.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return res.json();
      })
      .then((data: FormPayload) => {
        if (!cancelled) {
          setPayload(data);
          setCompleterName(data.recipient_name ?? '');
          setCompleterEmail(data.recipient_email ?? '');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? 'Invalid or expired link');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !payload?.content?.questions) return;
    if (!completerName.trim()) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Please enter your name."
          level="error"
        />
      ));
      return;
    }
    if (!completerEmail.trim()) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Please enter your email."
          level="error"
        />
      ));
      return;
    }
    if (hasProviderSignatureQuestion) {
      if (!signatureName.trim()) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message="Please enter your name to sign."
            level="error"
          />
        ));
        const firstSigIdx = questions.findIndex(
          (q) => q.questionType === 'ProviderSignature',
        );
        if (firstSigIdx >= 0) {
          document
            .getElementById(`question-${firstSigIdx}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (!signatureRole.trim()) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message="Please enter your role to sign."
            level="error"
          />
        ));
        const firstSigIdx = questions.findIndex(
          (q) => q.questionType === 'ProviderSignature',
        );
        if (firstSigIdx >= 0) {
          document
            .getElementById(`question-${firstSigIdx}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (!signatureMatchesRecipient) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message="Signature name must match the full name in Your details above."
            level="error"
          />
        ));
        const firstSigIdx = questions.findIndex(
          (q) => q.questionType === 'ProviderSignature',
        );
        if (firstSigIdx >= 0) {
          document
            .getElementById(`question-${firstSigIdx}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }

    const firstMissingRequiredIndex = questions.findIndex((q, idx) => {
      const required = q.isRequired !== false;
      if (
        !required ||
        q.questionType === 'Statement' ||
        q.questionType === 'ProviderSignature'
      )
        return false;
      if (q.questionType === 'FileUpload') return !fileAnswers[idx];
      const val = answers[idx] ?? '';
      return !String(val).trim();
    });
    if (firstMissingRequiredIndex >= 0) {
      const q = questions[firstMissingRequiredIndex]!;
      const label =
        q.questionType === 'FileUpload'
          ? 'Please upload a file for the required question.'
          : 'Please answer all required questions.';
      toast.custom(({ visible }) => (
        <ToastAlert isVisible={visible} message={label} level="error" />
      ));
      const el = document.getElementById(
        `question-${firstMissingRequiredIndex}`,
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const answerList: AnswerInput[] = questions.map((q, idx) => {
      const questionType = q.questionType ?? 'FreeText';
      if (questionType === 'FileUpload') {
        const file = fileAnswers[idx];
        return {
          question: q.question,
          questionType: 'FileUpload',
          ...(file && { mediaId: file.mediaId, fileName: file.fileName }),
        };
      }
      const val = answers[idx] ?? '';
      if (questionType === 'MultiSelect') {
        return {
          question: q.question,
          questionType,
          answers: val ? val.split(',').map((s) => s.trim()) : [],
        };
      }
      return {
        question: q.question,
        questionType,
        answer: val,
      };
    });

    setSubmitting(true);
    try {
      const res = await fetch(`${FORM_SUBMISSION_URL}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          answers: answerList,
          completer_name: completerName.trim(),
          completer_email: completerEmail.trim(),
          ...(hasProviderSignatureQuestion && {
            signature_name: signatureName.trim(),
            signature_role: signatureRole.trim(),
          }),
        }),
      });
      if (!res.ok) {
        let message = 'Submission failed';
        try {
          const errorData = await res.json();
          message = errorData?.message ?? message;
        } catch {
          const text = await res.text();
          if (text) message = text;
        }
        throw new Error(message);
      }
      await res.json();
      setSubmitted(true);
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={
            err instanceof Error
              ? err.message
              : 'Something went wrong. Please try again.'
          }
          level="error"
        />
      ));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer noPadding containerClassName="pt-16">
          <div className="flex flex-col items-center">
            <IllustrationWarning className="mt-14 mb-6 text-center text-orange-100" />
            <div className="mb-3 text-center font-serif text-subtitle font-light text-neutral-150">
              Invalid link
            </div>
            <div className="w-2/3 text-center text-paragraph text-neutral-125">
              This form link is missing a token. Please use the link you
              received.
            </div>
          </div>
        </PageContainer>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer noPadding containerClassName="pt-16">
          <div className="flex justify-center pt-24">
            <Spinner />
          </div>
        </PageContainer>
      </>
    );
  }

  if (error || !payload) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer noPadding containerClassName="pt-16">
          <div className="flex flex-col items-center">
            <IllustrationWarning className="mt-14 mb-6 text-center text-orange-100" />
            <div className="mb-3 text-center font-serif text-subtitle font-light text-neutral-150">
              Uh oh!
            </div>
            <div className="w-2/3 text-center text-paragraph text-neutral-125">
              This link is invalid or expired. Please ask the person who sent it
              for a new link.
            </div>
          </div>
        </PageContainer>
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer noPadding containerClassName="pt-16">
          <div className="flex flex-col items-center">
            <IllustrationCheckmark className="mt-14 mb-6 text-center text-secondary-100" />
            <div className="mb-3 text-center font-serif text-subtitle font-light text-neutral-150">
              Thank you!
            </div>
            <div className="w-2/3 text-center text-paragraph text-neutral-125">
              Your responses have been submitted. You can close this window.
            </div>
          </div>
        </PageContainer>
      </>
    );
  }

  const branding = payload.branding ?? null;

  return (
    <>
      <UnauthedHeader branding={branding} />
      <PageContainer noPadding containerClassName="pt-16">
        <form
          onSubmit={handleSubmit}
          className="flex min-h-[calc(100vh-4rem)] flex-col text-neutral-125 lg:flex-row"
        >
          {/* Left sidebar: scrolls with the page on mobile; sticky two-column on desktop */}
          <div
            data-testid="form-submission-details"
            className="flex w-full flex-col p-6 lg:sticky lg:top-16 lg:h-full lg:w-1/3 lg:min-w-[400px] lg:pl-20"
          >
            <h1 className="mb-2 font-serif text-subtitle-small font-light text-green-150">
              {payload.form_name}
            </h1>
            <p className="mb-4 text-caption text-neutral-200">
              {payload.provider_name || payload.patient_first_name
                ? [
                    payload.provider_name,
                    (payload.patient_full_name ?? payload.patient_first_name) &&
                      `has sent you this form to complete for ${
                        payload.patient_full_name ?? payload.patient_first_name
                      }`,
                  ]
                    .filter(Boolean)
                    .join(' ')
                : 'Complete this form. Your name and email will be recorded.'}
            </p>
            <div
              className="mb-4 rounded-xl border border-neutral-50 p-4"
              style={
                branding?.primary_color
                  ? { borderColor: branding.primary_color }
                  : undefined
              }
            >
              <div className="mb-2 text-caption font-medium text-neutral-200">
                Your details
              </div>
              <InputGroup
                placeholder="Your name"
                containerClassName="mb-3 w-full"
                className="rounded-lg"
                value={completerName}
                onChange={(e) => setCompleterName(e.target.value)}
                readOnly={
                  !!(payload?.recipient_name && payload?.recipient_email)
                }
              />
              <InputGroup
                placeholder="Your email"
                inputType="email"
                containerClassName="w-full"
                className="rounded-lg"
                value={completerEmail}
                onChange={(e) => setCompleterEmail(e.target.value)}
                readOnly={
                  !!(payload?.recipient_name && payload?.recipient_email)
                }
              />
            </div>
            <div className="mt-6 flex flex-row items-center justify-end">
              <Button
                type="submit"
                title="Complete form"
                size="small"
                disabled={
                  submitting ||
                  !completerName.trim() ||
                  !completerEmail.trim() ||
                  (hasProviderSignatureQuestion &&
                    (!signatureName.trim() ||
                      !signatureRole.trim() ||
                      !signatureMatchesRecipient)) ||
                  !allRequiredAnswered
                }
                IconComponent={submitting ? Spinner : undefined}
                style={
                  branding?.secondary_color
                    ? {
                        backgroundColor: branding.secondary_color,
                        borderColor: branding.secondary_color,
                        color: '#fff',
                      }
                    : undefined
                }
              />
            </div>
          </div>

          {/* Right: questions — same card style as CompletePractitionerForm */}
          <div
            data-testid="form-submission-questions"
            className="w-full p-6 lg:w-2/3 lg:overflow-y-auto"
          >
            <div className="mx-auto max-w-[800px]">
              {payload.instructional_text && (
                <div className="mb-4 text-caption text-neutral-125">
                  {payload.instructional_text}
                </div>
              )}
              <div className="grid gap-y-6">
                {questions.map((q, idx) => {
                  const isStatement = q.questionType === 'Statement';
                  return (
                    <div
                      key={`${idx}-${q.question}`}
                      id={`question-${idx}`}
                      className={
                        !isStatement
                          ? 'rounded-lg border border-neutral-75 p-4'
                          : ''
                      }
                    >
                      <div
                        className={
                          !isStatement
                            ? 'prose prose-sm max-w-none border-b border-neutral-75 pb-1 text-body text-green-150'
                            : 'prose prose-sm mt-4 max-w-none text-body text-green-150'
                        }
                        dangerouslySetInnerHTML={{
                          __html: parseMarkdown(q.question || ''),
                        }}
                      />
                      {!isStatement && (
                        <>
                          {(q.questionType === 'FreeText' ||
                            q.questionType === 'RichText' ||
                            !q.questionType) && (
                            <textarea
                              data-testid={`form-submission-answer-${idx}`}
                              className="w-full rounded-md border border-neutral-50 bg-neutral-50 p-3 text-body text-green-150 shadow-sm focus:border-green-50 focus:ring-2 focus:ring-secondary-50 lg:max-w-[398px]"
                              rows={3}
                              value={answers[idx] ?? ''}
                              onChange={(e) =>
                                setAnswers((prev) => ({
                                  ...prev,
                                  [idx]: e.target.value,
                                }))
                              }
                            />
                          )}
                          {q.questionType === 'MultipleChoice' && (
                            <input
                              type="text"
                              className="w-full rounded-md border border-neutral-50 bg-neutral-50 p-3 text-body text-green-150 shadow-sm focus:border-green-50 focus:ring-2 focus:ring-secondary-50 lg:max-w-[398px]"
                              value={answers[idx] ?? ''}
                              onChange={(e) =>
                                setAnswers((prev) => ({
                                  ...prev,
                                  [idx]: e.target.value,
                                }))
                              }
                            />
                          )}
                          {q.questionType === 'MultiSelect' && (
                            <input
                              type="text"
                              className="w-full rounded-md border border-neutral-50 bg-neutral-50 p-3 text-body text-green-150 shadow-sm focus:border-green-50 focus:ring-2 focus:ring-secondary-50 lg:max-w-[398px]"
                              placeholder="Comma-separated values"
                              value={answers[idx] ?? ''}
                              onChange={(e) =>
                                setAnswers((prev) => ({
                                  ...prev,
                                  [idx]: e.target.value,
                                }))
                              }
                            />
                          )}
                          {q.questionType === 'ProviderSignature' && (
                            <div className="mt-2 space-y-3">
                              <div className="mb-3 text-caption font-medium text-green-150">
                                Type your name,{' '}
                                <span className="font-medium">
                                  {completerName.trim() || 'your full name'}
                                </span>
                                , to sign electronically:
                              </div>
                              <InputGroup
                                placeholder="Your name (to sign)"
                                IconLeft={SignatureFlagName}
                                containerClassName="w-full lg:max-w-[398px]"
                                className="rounded-lg pl-12"
                                value={signatureName}
                                onChange={(e) =>
                                  setSignatureName(e.target.value)
                                }
                              />
                              <InputGroup
                                placeholder="Your role (e.g. Therapist, Psychologist)"
                                IconLeft={SignatureFlagRole}
                                containerClassName="w-full lg:max-w-[398px]"
                                className="rounded-lg pl-12"
                                value={signatureRole}
                                onChange={(e) =>
                                  setSignatureRole(e.target.value)
                                }
                              />
                              {signatureName.trim() && signatureRole.trim() ? (
                                <CompletedSignature
                                  isCurrentSigner
                                  signatureName={signatureName.trim()}
                                  signatureRole={signatureRole.trim()}
                                  nowFormattedTimestamp={getTimeStamp(
                                    new Date().toISOString(),
                                    false,
                                    false,
                                    true,
                                  )}
                                  className="mt-2"
                                />
                              ) : null}
                            </div>
                          )}
                          {q.questionType === 'FileUpload' && (
                            <div className="space-y-2">
                              <input
                                type="file"
                                className="w-full text-body text-green-150 file:mr-3 file:rounded-md file:border-0 file:bg-secondary-20 file:px-4 file:py-2 file:text-caption file:font-medium file:text-green-150"
                                disabled={uploadingFileIdx !== null}
                                accept={
                                  Array.isArray(q.allowedExtensions)
                                    ? q.allowedExtensions.join(',')
                                    : undefined
                                }
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleFileUpload(idx, f);
                                  e.target.value = '';
                                }}
                              />
                              {uploadingFileIdx === idx && (
                                <span className="flex items-center gap-2 text-caption text-neutral-200">
                                  <Spinner className="h-4 w-4" />
                                  Uploading…
                                </span>
                              )}
                              {fileAnswers[idx] && uploadingFileIdx !== idx && (
                                <div className="flex items-center justify-between rounded-md border border-neutral-75 bg-neutral-50 px-3 py-2">
                                  <span className="truncate text-caption text-green-150">
                                    {fileAnswers[idx].fileName}
                                  </span>
                                  <button
                                    type="button"
                                    className="shrink-0 text-caption text-neutral-200 underline hover:text-neutral-150"
                                    onClick={() =>
                                      setFileAnswers((prev) => {
                                        const next = { ...prev };
                                        delete next[idx];
                                        return next;
                                      })
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </form>
      </PageContainer>
    </>
  );
};

export default FormSubmission;
